import { EFinancingProgram } from '@if/core/types'
import { Grid } from '@mui/material'
import { memo, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ActionsDialog, InputTextField, InputTextFieldWithMask, SelectComponent } from '../../../../../../../components'
import { useCurrentLang, useJobTypeEnum } from '../../../../../../../data/selectors/app-config-selectors'
import { Address, ApplicantJob, useJobSchemaResolver } from '../../../../../../../data/types'
import { EMask } from '../../../../../../../data/types/constants'
import AddressComponent from './AddressComponent'

interface Props {
  open: boolean
  title: string
  defaultValue: ApplicantJob
  onConfirm: (data: ApplicantJob) => void
  onCancel: () => void
  editDisabled: boolean
  financingProgramId: EFinancingProgram | null
}

const EditJobDialog = ({ defaultValue, onConfirm, onCancel, open, title, editDisabled, financingProgramId }: Props) => {
  const { t } = useTranslation()
  const jobTypeEnum = useJobTypeEnum(financingProgramId)
  const lang = useCurrentLang()

  const resolver = useJobSchemaResolver({ financingProgramId })

  const {
    register,
    trigger,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm<ApplicantJob>({
    mode: 'onBlur',
    defaultValues: defaultValue,
    resolver,
    context: { financingProgramId },
  })

  useEffect(() => {
    reset(defaultValue)
  }, [defaultValue, reset])

  const isAddressEmpty = (address: Address | null) => {
    return address && Object.values(address).every((a) => a === null || a === undefined)
  }

  const handleSubmit = async () => {
    const isValid = await trigger([
      'annualSalary',
      'jobType',
      'jobTitle',
      'employerName',
      'employerPhone',
      'years',
      'months',
    ])
    if (isValid) {
      const jobCopy = { ...getValues() }
      if (isAddressEmpty(jobCopy.address)) jobCopy.address = null
      onConfirm(jobCopy)
    }
  }

  return (
    <ActionsDialog title={title} open={open} onCancel={onCancel} onConfirm={handleSubmit}>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.annualSalary}
            label={t('editCreditApplication.income.job.annualSalary')}
            {...register('annualSalary')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <SelectComponent
            items={Object.values(jobTypeEnum).map((item) => ({ label: item[lang], value: item.id }))}
            label={t('editCreditApplication.income.job.jobType')}
            {...register('jobType')}
            error={errors?.jobType}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            maxLength={50}
            error={errors?.jobTitle}
            label={t('editCreditApplication.income.job.jobTitle')}
            {...register('jobTitle')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.employerName}
            maxLength={100}
            label={t('editCreditApplication.income.job.employerName')}
            {...register('employerName')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Controller
            name="employerPhone"
            control={control}
            render={({ field }) => (
              <InputTextFieldWithMask
                mask={EMask.phoneNumberMask}
                error={errors?.employerPhone}
                label={t('editCreditApplication.income.job.employerPhone')}
                {...field}
                onChange={(e) => field.onChange(e.target.value.substring(1).replace(/-/g, ''))}
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            xs: 3,
            md: 1,
          }}
        >
          <InputTextField
            error={errors?.employerPhoneExt}
            label={t('editCreditApplication.income.job.employerPhoneExt')}
            {...register('employerPhoneExt')}
            maxLength={10}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.years}
            label={t('editCreditApplication.income.job.yearsAtThisJob')}
            {...register('years')}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.months}
            label={t('editCreditApplication.address.since.months')}
            {...register('months')}
          />
        </Grid>
        <AddressComponent
          formControl={control}
          register={register}
          errors={errors?.address}
          isCommercial
          name="address"
          includeDuration={false}
          editDisabled={editDisabled}
        />
      </Grid>
    </ActionsDialog>
  )
}

export default memo(EditJobDialog)
